import { Invoice, Workspace } from '../types';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { generateInvoicePDF } from '../utils/pdfGenerator';

interface EmailConfig {
  resendApiKey: string;
  fromEmail: string;
  fromName: string;
}

interface EmailData {
  to: string;
  subject: string;
  html: string;
  text?: string;
  attachments?: Array<{
    filename: string;
    content: string;
    contentType: string;
  }>;
}

class EmailService {
  private config: EmailConfig | null = null;

  setConfig(config: EmailConfig) {
    this.config = config;
  }

  // New method to set config from workspace settings
  setConfigFromWorkspace(workspace: Workspace) {
    if (!workspace.settings?.resendApiKey) {
      alert('No Resend API key found in workspace settings. Please add your business info in settings.');
      return false;
    }

    if (!workspace.settings?.fromEmail) {
      alert('No from email found in workspace settings. Please add your business info in settings.');
      return false;
    }
    if (!workspace.settings?.fromName) {
      alert('No from name found in workspace settings. Please add your business info in settings.');
      return false;
    }

    this.setConfig({
      resendApiKey: workspace.settings.resendApiKey,
      fromEmail: workspace.settings.fromEmail,
      fromName: workspace.settings.fromName
    });

    return true;
  }

  async sendEmail(data: EmailData): Promise<boolean> {
    if (!this.config) {
      alert('Email service not configured. Please set up your Resend API key and business info in workspace settings.');
      return false;
    }
    if (!this.config.fromEmail || !this.config.fromName) {
      alert('Missing business info (from email or from name). Please add your business info in settings.');
      return false;
    }

    // Debug: Log all fields being sent
    console.debug('[EmailService] Sending email with fields:', {
      resendApiKey: this.config.resendApiKey,
      fromEmail: this.config.fromEmail,
      fromName: this.config.fromName,
      to: data.to,
      subject: data.subject,
      html: data.html,
      text: data.text,
      attachments: data.attachments
    });

    // Check for missing required fields before sending
    if (!this.config.resendApiKey || !this.config.fromEmail || !this.config.fromName || !data.to || !data.subject || !data.html) {
      console.error('[EmailService] Missing required fields:', {
        resendApiKey: !!this.config.resendApiKey,
        fromEmail: !!this.config.fromEmail,
        fromName: !!this.config.fromName,
        to: !!data.to,
        subject: !!data.subject,
        html: !!data.html
      });
      alert('Missing required fields for sending email. Check console for details.');
      return false;
    }

    try {
      // Send email via backend API to avoid CORS
      const response = await fetch('https://api.dohatransittours.com/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resendApiKey: this.config.resendApiKey,
          fromEmail: this.config.fromEmail,
          fromName: this.config.fromName,
          to: data.to,
          subject: data.subject,
          html: data.html,
          text: data.text,
          attachments: data.attachments
        })
      });
      const result = await response.json();
      if (!response.ok || result.error) {
        throw new Error(result.error || 'Failed to send email');
      }
      console.log('Email sent successfully:', result);
      return true;
    } catch (error) {
      console.error('Error sending email:', error);
      if (error instanceof Error) {
        if (error.message.includes('API key')) {
          alert('Invalid Resend API key. Please check your API key in workspace settings.');
        } else if (error.message.includes('domain')) {
          alert('Domain not verified. Please verify your domain in Resend dashboard.');
        } else {
          alert(error.message);
        }
      } else {
        alert('Failed to send email.');
      }
      return false;
    }
  }

  async sendInvoiceEmail(invoice: Invoice, workspace: Workspace): Promise<boolean> {
    // Set config from workspace if not already set
    if (!this.config) {
      if (!this.setConfigFromWorkspace(workspace)) {
        console.error('[EmailService] Config not set from workspace');
        return false;
      }
    }

    if (!this.config) {
      console.error('[EmailService] Email service not configured');
      return false;
    }

    let downloadLink = invoice.downloadUrl;
    if (!downloadLink) {
      try {
        console.debug('[EmailService] No downloadUrl found on invoice, trying to get Firebase Storage URL...');
        // Use the exact same logic as copyFirebaseDownloadUrl
        const safeClientName = invoice.client?.name
          ?.toLowerCase()
          .replace(/[^a-z0-9]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '') || 'unnamed_client';
        const fileName = `invoices/${workspace.name}/${safeClientName}/Invoice_${invoice.invoiceNumber}.pdf`;
        const storageRef = ref(storage, fileName);
        try {
          downloadLink = await getDownloadURL(storageRef);
          console.debug('[EmailService] Got Firebase downloadUrl:', downloadLink);
        } catch (err: any) {
          if (err.code === 'storage/object-not-found') {
            console.debug('[EmailService] PDF not found, generating...');
            await generateInvoicePDF(invoice, workspace);
            downloadLink = await getDownloadURL(storageRef);
            console.debug('[EmailService] Generated and got downloadUrl:', downloadLink);
          } else {
            console.error('[EmailService] Failed to get Firebase download URL for email:', err);
            return false;
          }
        }
      } catch (err) {
        console.error('[EmailService] Unexpected error getting/generating download URL:', err);
        return false;
      }
    } else {
      console.debug('[EmailService] Using existing downloadUrl:', downloadLink);
    }

    console.log('[EmailService] About to render email HTML', { invoice, workspace, downloadLink });
    let html: string = '';
    try {
      // Use a template string for the email HTML instead of React Email
      html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px #eee; padding: 32px;">
          <h1 style="font-size: 24px; font-weight: bold; color: ${workspace.settings?.branding?.primaryColor || '#333'}; margin-bottom: 0;">${workspace.settings?.branding?.companyName || workspace.name}</h1>
          <div style="font-size: 14px; color: #555; margin: 0;">${workspace.settings?.branding?.address || ''}</div>
          <div style="font-size: 14px; color: #555; margin: 0;">${workspace.settings?.branding?.email || ''}</div>
          <div style="font-size: 14px; color: #555; margin: 0;">${workspace.settings?.branding?.phone || ''}</div>
          <hr style="margin: 24px 0; border: none; border-top: 1px solid #eee;" />
          <h2 style="font-size: 20px; font-weight: bold; color: ${workspace.settings?.branding?.primaryColor || '#333'}; margin-bottom: 0;">Invoice #${invoice.invoiceNumber}</h2>
          <div style="font-size: 16px; color: #333; margin: 8px 0 0 0;">Dear ${invoice.client.name},</div>
          <div style="font-size: 15px; color: #333; margin: 8px 0 0 0;">Please find your invoice for <b>${invoice.total} ${workspace.settings?.currency || 'USD'}</b> below.</div>
          ${invoice.paymentLink ? `<div style="font-size: 15px; color: #333; margin: 8px 0 0 0;">You can pay this invoice online at: <a href="${invoice.paymentLink}" style="color: ${workspace.settings?.branding?.primaryColor || '#007bff'};">${invoice.paymentLink}</a></div>` : ''}
          <div style="font-size: 15px; color: #333; margin: 8px 0 0 0;"><b>Due date:</b> ${new Date(invoice.dueDate).toLocaleDateString()}</div>
          <div style="margin: 24px 0;">
            <a href="${downloadLink}" style="background: ${workspace.settings?.branding?.primaryColor || '#007bff'}; color: #fff; font-size: 16px; padding: 12px 24px; border-radius: 6px; text-decoration: none; border: none; display: inline-block;">Download Invoice PDF</a>
          </div>
          <div style="font-size: 14px; color: #333; margin: 16px 0 0 0;">Thank you for your business!</div>
          <div style="font-size: 13px; color: #888; margin: 8px 0 0 0;">${workspace.settings?.branding?.companyName || workspace.name}</div>
        </div>
      `;
      console.log('[EmailService] Rendered HTML:', html);
    } catch (err) {
      console.error('[EmailService] Failed to render email HTML:', err);
      return false;
    }

    const subject = `Invoice ${invoice.invoiceNumber} from ${this.config.fromName}`;

    try {
      const result = await this.sendEmail({
        to: invoice.client.email,
        subject,
        html,
        text: html ? html.replace(/<[^>]*>/g, '') : ''
      });
      console.debug('[EmailService] Email sent result:', result);
      return result;
    } catch (err) {
      console.error('[EmailService] Failed to send email:', err);
      return false;
    }
  }

  async testEmailConfiguration(workspace?: Workspace): Promise<boolean> {
    // If workspace is provided, set config from it
    if (workspace && !this.config) {
      if (!this.setConfigFromWorkspace(workspace)) {
        return false;
      }
    }

    if (!this.config) {
      console.error('Email service not configured');
      return false;
    }

    const testData = {
      to: this.config.fromEmail,
      subject: 'Test Email Configuration - FreelancePay',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #333;">Test Email</h1>
        <p>This is a test email to verify your email configuration.</p>
        <p>If you received this email, your email settings are working correctly!</p>
          <p><strong>From:</strong> ${this.config.fromName} &lt;${this.config.fromEmail}&gt;</p>
          <p><strong>Sent at:</strong> ${new Date().toLocaleString()}</p>
        </div>
      `,
    };

    return this.sendEmail(testData);
  }

  async sendWorkspaceInvite(
    email: string,
    workspaceName: string,
    inviterName: string,
    inviteLink: string,
    workspace?: Workspace
  ): Promise<boolean> {
    // If workspace is provided, set config from it
    if (workspace && !this.config) {
      if (!this.setConfigFromWorkspace(workspace)) {
        return false;
      }
    }

    if (!this.config) {
      console.error('Email service not configured');
      return false;
    }

    const subject = `Invitation to join ${workspaceName} workspace`;
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #333;">Workspace Invitation</h1>
      <p>Hello,</p>
        <p>${inviterName} has invited you to join the <strong>${workspaceName}</strong> workspace.</p>
      <p>Click the link below to accept the invitation:</p>
        <p><a href="${inviteLink}" style="color: #007bff; text-decoration: none;">${inviteLink}</a></p>
        <p><em>This invitation will expire in 7 days.</em></p>
      <p>Best regards,<br>${this.config.fromName}</p>
      </div>
    `;

    return this.sendEmail({
      to: email,
      subject,
      html,
      text: html.replace(/<[^>]*>/g, '')
    });
  }

  // Method to check if email service is properly configured
  isConfigured(): boolean {
    return !!(this.config && this.config.resendApiKey);
  }

  // Method to get current configuration status
  getConfigStatus(): { configured: boolean; hasApiKey: boolean; hasFromEmail: boolean } {
    return {
      configured: this.isConfigured(),
      hasApiKey: !!(this.config?.resendApiKey),
      hasFromEmail: !!(this.config?.fromEmail)
    };
  }
}

export const emailService = new EmailService();

export const sendWorkspaceInvite = (
  email: string,
  workspaceName: string,
  inviterName: string,
  inviteLink: string,
  workspace?: Workspace
) => emailService.sendWorkspaceInvite(email, workspaceName, inviterName, inviteLink, workspace);